import { AllSupportedCitizenships, PersonalData } from "../../domain";
import { option, taskEither } from "fp-ts";
import { pipe } from "fp-ts/function";
import {
  Box,
  Button,
  LocalizedString,
  Space,
  useForm,
  useIsMobileLayout,
  Validator,
  validators,
} from "@dbn/omnichannel-design-system-g4p";
import { useFormatMessage } from "../../../intl";
import { FormState, PersonalDataForm } from "./PersonalDataForm";

import { Option } from "fp-ts/Option";
import { CountryCode, Sex } from "../../../globalDomain";
import { useValidators } from "../../../Common/useValidators";
import { isForeign } from "../../utils";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { DocumentType } from "../../../UploadDocuments/domain";
import { CloseIcon } from "@g4p/react-styleguide-library";
import "./styles.css";

const nameForbiddenChars = /[\^<>\\$#ß*%`˙~đĐ\[\]®™€×¤{}÷]/;

type Props = {
  initialValues: FormState;
  onValidate: Validator<PersonalData>;
  onSave: (personalData: PersonalData) => unknown;
  onCancel: () => unknown;
  displayCountryOfBirth: boolean;
  supportForeign: boolean;
  documentType: DocumentType;
};

export function PersonalDataFormEdit(props: Props) {
  const formatMessage = useFormatMessage();

  const {
    nonBlankString,
    defined,
    definedNoExtract,
    validBirthDate,
    validBirthNumber,
    maxLength,
  } = useValidators();

  const nameValidator = <T extends string>(
    errorMessage: LocalizedString
  ): Validator<T> =>
    validators.fromPredicate(s => !nameForbiddenChars.test(s), errorMessage);

  const { fieldProps, handleSubmit, formErrors } = useForm(
    {
      initialValues: props.initialValues,
      fieldValidators: values => {
        return {
          name: validators.inSequence(
            nonBlankString,
            validators.maxLength(
              25,
              formatMessage("Identification.personalData.nameLengthError")
            ),
            nameValidator(
              formatMessage("Form.fieldError.disableSpecialCharacters")
            )
          ),
          surname: validators.inSequence(
            nonBlankString,
            validators.maxLength(
              25,
              formatMessage("Identification.personalData.surnameLengthError")
            ),
            nameValidator(
              formatMessage("Form.fieldError.disableSpecialCharacters")
            )
          ),
          sex: defined<Sex>(),
          citizenship: defined<AllSupportedCitizenships>(),
          dateOfBirth: validators.inSequence(
            defined<Option<Date>>(),
            validBirthDate
          ),
          placeOfBirth: validators.inSequence(nonBlankString, maxLength(30)),
          countryOfBirth: props.displayCountryOfBirth
            ? definedNoExtract<CountryCode>()
            : undefined,
          birthNumber: isForeign(values.citizenship)
            ? undefined
            : validators.inSequence(
                defined<NonEmptyString>(),
                validBirthNumber
              ),
        };
      },
    },
    {
      onSubmit: values =>
        pipe(
          {
            ...values,
            birthNumber: NonEmptyString.is(values.birthNumber)
              ? option.some(values.birthNumber)
              : values.birthNumber,
            secondCitizenship: option.none,
          },
          props.onValidate,
          taskEither.chain(values =>
            taskEither.fromIO(() => props.onSave(values))
          )
        ),
    }
  );
  const WrappedIcon = ({ size }: { size: "default" }) => {
    const iconSize = size === "default" ? 14 : 14;
    return <CloseIcon width={iconSize} height={iconSize} />;
  };

  const isMobileLayout = useIsMobileLayout();

  const cancelButton = (
    <Button
      variant="text"
      size="default"
      icon={WrappedIcon}
      action={props.onCancel}
      label={formatMessage("Cancel")}
    />
  );

  const saveButton = (
    <Button
      variant="primary"
      size="default"
      action={handleSubmit}
      label={formatMessage("Save")}
    />
  );

  return (
    <Box column>
      <PersonalDataForm
        fieldProps={fieldProps}
        displayCountryOfBirth={props.displayCountryOfBirth}
        readOnly={false}
        errors={formErrors}
        reworkPersonalData={option.none}
        supportForeign={props.supportForeign}
        documentType={props.documentType}
      />
      <Space units={5} />

      <Box
        column={isMobileLayout}
        grow
        shrink
        width={isMobileLayout ? "100%" : undefined}
        vAlignContent="center"
        hAlignContent={isMobileLayout ? "center" : "right"}
      >
        {isMobileLayout ? saveButton : cancelButton}
        <Space units={4} />
        {isMobileLayout ? cancelButton : saveButton}
      </Box>
    </Box>
  );
}
